import React, { useEffect, useState } from 'react';
import { collection, query, onSnapshot, doc, updateDoc, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { QueueConfig } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { toast } from '@/lib/toast';

type ConfigSection = 'timeouts' | 'penalties' | 'loyalty' | 'queue' | 'notifications' | 'business';

interface ConfigChange {
 section: string;
 field: string;
 oldValue: any;
 newValue: any;
}

const SystemConfiguration: React.FC = () => {
 const { user } = useAuth();
 const [config, setConfig] = useState<QueueConfig | null>(null);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [editedConfig, setEditedConfig] = useState<QueueConfig | null>(null);
 const [hasChanges, setHasChanges] = useState(false);
 const [activeSection, setActiveSection] = useState<ConfigSection>('timeouts');
 const [showPreview, setShowPreview] = useState(false);
 const [changes, setChanges] = useState<ConfigChange[]>([]);

 useEffect(() => {
 const configQuery = query(collection(db, 'queue_config'));

 const unsubscribe = onSnapshot(configQuery, async (snapshot) => {
 if (!snapshot.empty) {
 const configDoc = snapshot.docs[0];
 const configData = {
 configId: configDoc.id,
 ...configDoc.data(),
 } as QueueConfig;

 setConfig(configData);
 setEditedConfig(configData);
 setLoading(false);
 } else {
 // Create default configuration if none exists
 try {
 const defaultConfig = {
 timeouts: {
 arrivalWindow: 10,
 courtesyTime: 5,
 expirationTime: 120,
 notificationAdvance: 15,
 },
 penalties: {
 noShow: -10,
 lateArrival: -5,
 lateCancellation: -3,
 },
 loyalty: {
 pointsPerService: 10,
 vipThreshold: 100,
 bonusMultiplier: 1.5,
 },
 queueBehavior: {
 maxQueueSize: 50,
 enableVIPPriority: true,
 enableBarberSelection: true,
 autoAssignBarber: true,
 },
 notifications: {
 enablePushNotifications: true,
 enableEmailNotifications: true,
 enableSMSNotifications: false,
 positionUpdateThreshold: 3,
 },
 businessRules: {
 minServiceDuration: 15,
 maxServiceDuration: 180,
 allowAnonymousQueue: true,
 requireServiceSelection: false,
 },
 createdAt: Timestamp.now(),
 updatedAt: Timestamp.now(),
 };

 await addDoc(collection(db, 'queue_config'), defaultConfig);
 } catch (error) {
 console.error('Error creating default configuration:', error);
 setLoading(false);
 }
 }
 });

 return () => unsubscribe();
 }, []);

 const handleChange = (section: keyof QueueConfig, field: string, value: any) => {
 if (!editedConfig) return;

 const newConfig = {
 ...editedConfig,
 [section]: {
 ...(editedConfig[section] as any),
 [field]: value,
 },
 };

 setEditedConfig(newConfig);
 setHasChanges(true);

 // Track changes for preview
 const changeList: ConfigChange[] = [];
 if (config) {
 const oldValue = (config[section] as any)?.[field];
 if (oldValue !== value) {
 changeList.push({
 section: section as string,
 field,
 oldValue,
 newValue: value,
 });
 }
 }
 setChanges(changeList);
 };

 const handleSave = async () => {
 if (!editedConfig || !user) return;

 setSaving(true);

 try {
 const configRef = doc(db, 'queue_config', editedConfig.configId);
 await updateDoc(configRef, {
 ...editedConfig,
 updatedBy: user.id,
 updatedAt: Timestamp.now(),
 });

 setHasChanges(false);
 setChanges([]);
 setShowPreview(false);
 toast.success('Configuration saved successfully');
 } catch (error) {
 console.error('Error saving configuration:', error);
 toast.error('Failed to save configuration. Please try again.');
 } finally {
 setSaving(false);
 }
 };

 const handleReset = () => {
 setEditedConfig(config);
 setHasChanges(false);
 setChanges([]);
 toast.info('Changes reset to current values');
 };

 const handleResetToDefaults = async () => {
 if (!editedConfig || !user) return;

 const defaults: Partial<QueueConfig> = {
 timeouts: {
 arrivalWindow: 10,
 courtesyTime: 5,
 expirationTime: 120,
 notificationAdvance: 15,
 },
 penalties: {
 noShow: -10,
 lateArrival: -5,
 lateCancellation: -3,
 },
 loyalty: {
 pointsPerService: 10,
 vipThreshold: 100,
 bonusMultiplier: 1.5,
 },
 queueBehavior: {
 maxQueueSize: 50,
 enableVIPPriority: true,
 enableBarberSelection: true,
 autoAssignBarber: true,
 },
 notifications: {
 enablePushNotifications: true,
 enableEmailNotifications: true,
 enableSMSNotifications: false,
 positionUpdateThreshold: 3,
 },
 businessRules: {
 minServiceDuration: 15,
 maxServiceDuration: 180,
 allowAnonymousQueue: true,
 requireServiceSelection: false,
 },
 };

 try {
 const configRef = doc(db, 'queue_config', editedConfig.configId);
 await updateDoc(configRef, {
 ...defaults,
 updatedBy: user.id,
 updatedAt: Timestamp.now(),
 });

 toast.success('Configuration reset to defaults successfully');
 } catch (error) {
 console.error('Error resetting configuration:', error);
 toast.error('Failed to reset configuration. Please try again.');
 }
 };

 if (loading) {
 return (
 <div className="flex items-center justify-center h-96">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info/30 mx-auto"></div>
 <p className="mt-4 text-ink-muted">Loading configuration...</p>
 </div>
 </div>
 );
 }

 if (!editedConfig) {
 return (
 <div className="flex items-center justify-center h-96">
 <p className="text-ink-muted">No configuration found</p>
 </div>
 );
 }

 const sections = [
 { id: 'timeouts' as ConfigSection, label: 'Timeouts', icon: '⏱️' },
 { id: 'penalties' as ConfigSection, label: 'Penalties', icon: '⚠️' },
 { id: 'loyalty' as ConfigSection, label: 'Loyalty', icon: '🎁' },
 { id: 'queue' as ConfigSection, label: 'Queue Behavior', icon: '🎫' },
 { id: 'notifications' as ConfigSection, label: 'Notifications', icon: '🔔' },
 { id: 'business' as ConfigSection, label: 'Business Rules', icon: '📋' },
 ];

 return (
 <div className="p-6 space-y-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h2 className="text-2xl font-bold text-ink">System Configuration</h2>
 <p className="text-sm text-ink-muted mt-1 text-ink-muted">
 Configure queue system parameters and behavior
 </p>
 </div>
 <div className="flex space-x-2">
 {hasChanges && (
 <>
 <button
 onClick={() => setShowPreview(true)}
 className="px-4 py-2 text-sm font-medium text-info bg-info/10 rounded-lg hover:bg-info/20 transition-colors"
 >
 Preview Changes
 </button>
 <button
 onClick={handleReset}
 className="px-4 py-2 text-sm font-medium text-ink-muted bg-surface-2 rounded-lg hover:bg-surface-3 transition-colors bg-surface-2 text-ink-muted"
 >
 Reset
 </button>
 </>
 )}
 <button
 onClick={handleResetToDefaults}
 className="px-4 py-2 text-sm font-medium text-warning bg-warning/10 rounded-lg hover:bg-warning/20 transition-colors"
 >
 Reset to Defaults
 </button>
 <button
 onClick={handleSave}
 disabled={!hasChanges || saving}
 className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
 hasChanges && !saving
 ? 'bg-info hover:bg-info/90'
 : 'bg-surface-3 cursor-not-allowed'
 }`}
 >
 {saving ? 'Saving...' : 'Save Configuration'}
 </button>
 </div>
 </div>

 {/* Change indicator */}
 {hasChanges && (
 <div className="bg-accent/10 border border-accent/30 rounded-lg p-3 flex items-center">
 <span className="text-accent text-sm">
 ⚠️ You have unsaved changes. Click"Save Configuration" to apply them.
 </span>
 </div>
 )}

 {/* Section Tabs */}
 <div className="border-b border-hairline">
 <nav className="flex space-x-4 overflow-x-auto">
 {sections.map((section) => (
 <button
 key={section.id}
 onClick={() => setActiveSection(section.id)}
 className={`whitespace-nowrap py-3 px-4 font-medium text-sm transition-colors flex items-center space-x-2 ${
 activeSection === section.id
 ? 'border-b-2 border-info/30 text-info'
 : 'text-ink-subtle hover:text-ink'
 }`}
 >
 <span>{section.icon}</span>
 <span>{section.label}</span>
 </button>
 ))}
 </nav>
 </div>

 {/* Configuration Forms */}
 <div className="bg-white rounded-lg border border-hairline p-6 bg-surface-2 border-hairline">
 {/* Timeouts */}
 {activeSection === 'timeouts' && (
 <div className="space-y-4">
 <h3 className="text-lg font-semibold text-ink mb-4">Timeout Settings</h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Arrival Window (minutes)
 </label>
 <input
 type="number"
 value={editedConfig.timeouts.arrivalWindow}
 onChange={(e) =>
 handleChange('timeouts', 'arrivalWindow', parseInt(e.target.value))
 }
 min="1"
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">Time for client to arrive after notification</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Courtesy Time (minutes)
 </label>
 <input
 type="number"
 value={editedConfig.timeouts.courtesyTime}
 onChange={(e) =>
 handleChange('timeouts', 'courtesyTime', parseInt(e.target.value))
 }
 min="1"
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">Grace period before marking no-show</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Expiration Time (minutes)
 </label>
 <input
 type="number"
 value={editedConfig.timeouts.expirationTime}
 onChange={(e) =>
 handleChange('timeouts', 'expirationTime', parseInt(e.target.value))
 }
 min="1"
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">Time before ticket auto-expires</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Notification Advance (minutes)
 </label>
 <input
 type="number"
 value={editedConfig.timeouts.notificationAdvance}
 onChange={(e) =>
 handleChange('timeouts', 'notificationAdvance', parseInt(e.target.value))
 }
 min="1"
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">How far ahead to notify clients</p>
 </div>
 </div>
 </div>
 )}

 {/* Penalties */}
 {activeSection === 'penalties' && (
 <div className="space-y-4">
 <h3 className="text-lg font-semibold text-ink mb-4">Penalty Points</h3>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 No-Show Penalty
 </label>
 <input
 type="number"
 value={editedConfig.penalties.noShow}
 onChange={(e) => handleChange('penalties', 'noShow', parseInt(e.target.value))}
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">Points deducted for no-show</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Late Arrival Penalty
 </label>
 <input
 type="number"
 value={editedConfig.penalties.lateArrival}
 onChange={(e) =>
 handleChange('penalties', 'lateArrival', parseInt(e.target.value))
 }
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">Points deducted for late arrival</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Late Cancellation Penalty
 </label>
 <input
 type="number"
 value={editedConfig.penalties.lateCancellation}
 onChange={(e) =>
 handleChange('penalties', 'lateCancellation', parseInt(e.target.value))
 }
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">Points deducted for late cancellation</p>
 </div>
 </div>
 </div>
 )}

 {/* Loyalty */}
 {activeSection === 'loyalty' && (
 <div className="space-y-4">
 <h3 className="text-lg font-semibold text-ink mb-4">Loyalty Program</h3>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Points Per Service
 </label>
 <input
 type="number"
 value={editedConfig.loyalty.pointsPerService}
 onChange={(e) =>
 handleChange('loyalty', 'pointsPerService', parseInt(e.target.value))
 }
 min="0"
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">Base points earned per service</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 VIP Threshold
 </label>
 <input
 type="number"
 value={editedConfig.loyalty.vipThreshold}
 onChange={(e) =>
 handleChange('loyalty', 'vipThreshold', parseInt(e.target.value))
 }
 min="0"
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">Points needed to become VIP</p>
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Bonus Multiplier
 </label>
 <input
 type="number"
 step="0.1"
 value={editedConfig.loyalty.bonusMultiplier}
 onChange={(e) =>
 handleChange('loyalty', 'bonusMultiplier', parseFloat(e.target.value))
 }
 min="1"
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">Multiplier for bonus promotions</p>
 </div>
 </div>
 </div>
 )}

 {/* Queue Behavior */}
 {activeSection === 'queue' && (
 <div className="space-y-4">
 <h3 className="text-lg font-semibold text-ink mb-4">Queue Behavior</h3>
 <div className="space-y-4">
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Maximum Queue Size
 </label>
 <input
 type="number"
 value={editedConfig.queueBehavior.maxQueueSize}
 onChange={(e) =>
 handleChange('queueBehavior', 'maxQueueSize', parseInt(e.target.value))
 }
 min="1"
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 </div>
 <div className="space-y-3">
 <label className="flex items-center">
 <input
 type="checkbox"
 checked={editedConfig.queueBehavior.enableVIPPriority}
 onChange={(e) =>
 handleChange('queueBehavior', 'enableVIPPriority', e.target.checked)
 }
 className="w-4 h-4 text-info border-hairline rounded focus:ring-focus bg-surface-2 border-hairline"
 />
 <span className="ml-2 text-sm font-medium text-ink-muted">
 Enable VIP Priority
 </span>
 </label>
 <label className="flex items-center">
 <input
 type="checkbox"
 checked={editedConfig.queueBehavior.enableBarberSelection}
 onChange={(e) =>
 handleChange('queueBehavior', 'enableBarberSelection', e.target.checked)
 }
 className="w-4 h-4 text-info border-hairline rounded focus:ring-focus bg-surface-2 border-hairline"
 />
 <span className="ml-2 text-sm font-medium text-ink-muted">
 Allow Barber Selection
 </span>
 </label>
 <label className="flex items-center">
 <input
 type="checkbox"
 checked={editedConfig.queueBehavior.autoAssignBarber}
 onChange={(e) =>
 handleChange('queueBehavior', 'autoAssignBarber', e.target.checked)
 }
 className="w-4 h-4 text-info border-hairline rounded focus:ring-focus bg-surface-2 border-hairline"
 />
 <span className="ml-2 text-sm font-medium text-ink-muted">
 Auto-Assign Barber
 </span>
 </label>
 </div>
 </div>
 </div>
 )}

 {/* Notifications */}
 {activeSection === 'notifications' && (
 <div className="space-y-4">
 <h3 className="text-lg font-semibold text-ink mb-4">Notification Settings</h3>
 <div className="space-y-4">
 <div className="space-y-3">
 <label className="flex items-center">
 <input
 type="checkbox"
 checked={editedConfig.notifications.enablePushNotifications}
 onChange={(e) =>
 handleChange('notifications', 'enablePushNotifications', e.target.checked)
 }
 className="w-4 h-4 text-info border-hairline rounded focus:ring-focus bg-surface-2 border-hairline"
 />
 <span className="ml-2 text-sm font-medium text-ink-muted">
 Enable Push Notifications
 </span>
 </label>
 <label className="flex items-center">
 <input
 type="checkbox"
 checked={editedConfig.notifications.enableEmailNotifications}
 onChange={(e) =>
 handleChange('notifications', 'enableEmailNotifications', e.target.checked)
 }
 className="w-4 h-4 text-info border-hairline rounded focus:ring-focus bg-surface-2 border-hairline"
 />
 <span className="ml-2 text-sm font-medium text-ink-muted">
 Enable Email Notifications
 </span>
 </label>
 <label className="flex items-center">
 <input
 type="checkbox"
 checked={editedConfig.notifications.enableSMSNotifications}
 onChange={(e) =>
 handleChange('notifications', 'enableSMSNotifications', e.target.checked)
 }
 className="w-4 h-4 text-info border-hairline rounded focus:ring-focus bg-surface-2 border-hairline"
 />
 <span className="ml-2 text-sm font-medium text-ink-muted">
 Enable SMS Notifications
 </span>
 </label>
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Position Update Threshold
 </label>
 <input
 type="number"
 value={editedConfig.notifications.positionUpdateThreshold}
 onChange={(e) =>
 handleChange(
 'notifications',
 'positionUpdateThreshold',
 parseInt(e.target.value)
 )
 }
 min="1"
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 <p className="text-xs text-ink-subtle mt-1 text-ink-muted">Notify every N position changes</p>
 </div>
 </div>
 </div>
 )}

 {/* Business Rules */}
 {activeSection === 'business' && (
 <div className="space-y-4">
 <h3 className="text-lg font-semibold text-ink mb-4">Business Rules</h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Min Service Duration (minutes)
 </label>
 <input
 type="number"
 value={editedConfig.businessRules.minServiceDuration}
 onChange={(e) =>
 handleChange('businessRules', 'minServiceDuration', parseInt(e.target.value))
 }
 min="1"
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1 text-ink-muted">
 Max Service Duration (minutes)
 </label>
 <input
 type="number"
 value={editedConfig.businessRules.maxServiceDuration}
 onChange={(e) =>
 handleChange('businessRules', 'maxServiceDuration', parseInt(e.target.value))
 }
 min="1"
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-focus"
 />
 </div>
 </div>
 <div className="space-y-3">
 <label className="flex items-center">
 <input
 type="checkbox"
 checked={editedConfig.businessRules.allowAnonymousQueue}
 onChange={(e) =>
 handleChange('businessRules', 'allowAnonymousQueue', e.target.checked)
 }
 className="w-4 h-4 text-info border-hairline rounded focus:ring-focus bg-surface-2 border-hairline"
 />
 <span className="ml-2 text-sm font-medium text-ink-muted">
 Allow Anonymous Queue
 </span>
 </label>
 <label className="flex items-center">
 <input
 type="checkbox"
 checked={editedConfig.businessRules.requireServiceSelection}
 onChange={(e) =>
 handleChange('businessRules', 'requireServiceSelection', e.target.checked)
 }
 className="w-4 h-4 text-info border-hairline rounded focus:ring-focus bg-surface-2 border-hairline"
 />
 <span className="ml-2 text-sm font-medium text-ink-muted">
 Require Service Selection
 </span>
 </label>
 </div>
 </div>
 )}
 </div>

 {/* Preview Modal */}
 {showPreview && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-surface-2">
 <div className="p-6 border-b border-hairline">
 <h3 className="text-xl font-semibold text-ink">Preview Changes</h3>
 </div>
 <div className="p-6">
 {changes.length > 0 ? (
 <div className="space-y-2">
 {changes.map((change, index) => (
 <div key={index} className="bg-surface-2 rounded p-3 bg-surface-2">
 <p className="text-sm font-medium text-ink">
 {change.section}.{change.field}
 </p>
 <p className="text-sm text-ink-muted mt-1 text-ink-muted">
 <span className="text-danger">{String(change.oldValue)}</span>
 {' → '}
 <span className="text-success">{String(change.newValue)}</span>
 </p>
 </div>
 ))}
 </div>
 ) : (
 <p className="text-ink-muted">No changes to preview</p>
 )}
 </div>
 <div className="p-6 border-t border-hairline flex justify-end border-hairline">
 <button
 onClick={() => setShowPreview(false)}
 className="px-4 py-2 text-sm font-medium text-ink-muted bg-surface-2 rounded-lg hover:bg-surface-2 text-ink-muted"
 >
 Close
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default SystemConfiguration;
